/**
 * Trend line chart for 1–2 series over time buckets.
 * `series`: [{ points: [{bucket, value}], color, label }].
 * All series must share the same bucket axis (same length/order).
 *   <LineChart series={[{ points, color:'#34C77B', label:'Opened' }]} />
 */
export const LineChart = ({ series = [], height = 160 }) => {
  const base = series[0]?.points ?? [];
  if (base.length < 2) {
    return <p class="py-8 text-center text-sm text-text-muted">Not enough data to chart.</p>;
  }

  const W = 640;
  const H = height;
  const padL = 30;
  const padB = 22;
  const padT = 8;
  const innerW = W - padL - 8;
  const innerH = H - padB - padT;

  const allVals = series.flatMap((s) => s.points.map((p) => p.value));
  const max = Math.max(...allVals, 1);
  const stepX = innerW / (base.length - 1);
  const x = (i) => padL + i * stepX;
  const y = (v) => padT + innerH - (v / max) * innerH;

  // Sparse x labels: ~6 across.
  const labelEvery = Math.ceil(base.length / 6);

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} class="w-full" style={{ height: 'auto' }}>
        {/* y gridlines at 0, mid, max */}
        {[0, 0.5, 1].map((f) => (
          <g key={f}>
            <line x1={padL} y1={y(max * f)} x2={W - 8} y2={y(max * f)} stroke="#2C2722" stroke-width="1" />
            <text x={padL - 5} y={y(max * f) + 3} text-anchor="end" class="fill-text-muted" font-size="9">
              {Math.round(max * f)}
            </text>
          </g>
        ))}
        {/* x labels */}
        {base.map((p, i) =>
          i % labelEvery === 0 ? (
            <text key={p.bucket} x={x(i)} y={H - 6} text-anchor="middle" class="fill-text-muted" font-size="9">
              {String(p.bucket).slice(5)}
            </text>
          ) : null,
        )}
        {/* series */}
        {series.map((s) => {
          const d = s.points.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`).join(' ');
          return (
            <path key={s.label} d={d} fill="none" stroke={s.color} stroke-width="2"
              stroke-linejoin="round" stroke-linecap="round" />
          );
        })}
      </svg>
      {series.length > 1 && (
        <div class="mt-1 flex flex-wrap gap-4">
          {series.map((s) => (
            <span key={s.label} class="flex items-center gap-1.5 text-xs text-text-secondary">
              <span class="inline-block h-2 w-2 rounded-full" style={{ background: s.color }} />
              {s.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
